import { useEffect, useRef, useState } from "react";

import { calculateReadingAccuracy, useSpeechRecognition } from "../../../../hooks/useSpeechRecognition";

// 문장 하나 듣고 따라 말하기(2026-10-17). '따라 말하기' 목록(SpeakingPanel)과 쪽 위 문장 끝 마이크
// 본문 따라 읽기(ReadAloud.jsx)가 함께 쓴다.
//
// - 마이크는 반드시 아이가 누른 자리에서 연다(iOS 는 사용자 동작 밖에서 열면 조용히 막힌다).
// - **쉼표에서 끊지 않는다.** 한 번 듣기(continuous 꺼짐)는 크롬이 짧게 쉬면 거기서 끝내서 문장 앞부분만
//   받았다("…tiny lights but" 64점). 이어 듣다가 문장 낱말을 다 들으면 스스로 끝내고, 아니면 '다 읽었어요' 로 끝낸다.
// - 다른 문장을 읽은 것이면 점수를 매기지 않는다. 듣는 중에 아이가 다음 줄을 읽어 버려 엉뚱한 줄에 점수가 붙었다.

// 문장 낱말 수에 맞춘 듣기 상한. 짧은 문장도 넉넉히 기다린다.
const listenMillisecondsOf = (text) => Math.max(8000, String(text).split(/\s+/).length * 900);
// 이 정확도가 되면 다 읽은 것으로 보고 듣기를 끝낸다
const DONE_ACCURACY = 95;
// 다른 문장이 이만큼 더 잘 맞으면 그 문장을 읽은 것으로 본다
const OTHER_SENTENCE_MARGIN = 30;

// sentenceList: 같은 음원의 문장들(다른 문장을 읽었는지 견줄 때 쓴다)
// onResult(sentence, accuracy, transcript, key)
const useSentenceSpeaking = ({ sentenceList, onResult }) => {
  const { isSupported, listen, finishListening, stopListening } = useSpeechRecognition();
  const [listeningKey, setListeningKey] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const playerRef = useRef(null);

  useEffect(
    () => () => {
      stopListening();
      if (playerRef.current) playerRef.current.pause();
    },
    [stopListening]
  );

  // key: 화면이 문장을 가르는 이름(여러 음원의 문장이 한 쪽에 있으면 음원 주소 + idx)
  const play = (sentence, key, onEnded) => {
    if (!sentence.mp3) return;
    if (playerRef.current) playerRef.current.pause();
    const player = new Audio(sentence.mp3);
    playerRef.current = player;
    setPlayingKey(key);
    const finish = (isEnded) => {
      setPlayingKey((current) => (current === key ? null : current));
      if (isEnded && onEnded) onEnded();
    };
    player.onended = () => finish(true);
    player.onerror = () => finish(false);
    player.play().catch(() => finish(false));
  };

  const stopPlaying = () => {
    if (playerRef.current) playerRef.current.pause();
    setPlayingKey(null);
  };

  const record = async (sentence, key, otherSentenceList = sentenceList) => {
    if (listeningKey !== null) {
      // 듣는 중에 다시 누르면 '다 읽었어요' 다. 여기까지 들은 것으로 채점한다.
      finishListening();
      return;
    }
    stopPlaying();
    setListeningKey(key);
    const { transcript } = await listen({
      language: "en-US",
      continuous: true,
      maximumMilliseconds: listenMillisecondsOf(sentence.text),
      onTranscript: (heard) => {
        if (calculateReadingAccuracy(sentence.text, heard) >= DONE_ACCURACY) finishListening();
      },
    });
    setListeningKey(null);
    if (!transcript) {
      alert("잘 안 들렸어요. 마이크를 다시 누르고 또박또박 읽어 주세요.");
      return;
    }
    const accuracy = calculateReadingAccuracy(sentence.text, transcript);
    const otherBest = Math.max(
      0,
      ...(otherSentenceList || [])
        .filter((other) => other.idx !== sentence.idx)
        .map((other) => calculateReadingAccuracy(other.text, transcript))
    );
    if (otherBest >= accuracy + OTHER_SENTENCE_MARGIN) {
      alert("다른 문장을 읽은 것 같아요. 이 문장을 다시 읽어 주세요.");
      return;
    }
    onResult(sentence, accuracy, transcript, key);
  };

  return { isSupported, listeningKey, playingKey, play, stopPlaying, record };
};

export default useSentenceSpeaking;
